import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import "./PromoCodeModal.scss";
import closeIcon from "../../../images/CloseCircle.svg";
import deleteIcon from "../../../images/DeleteIcon.svg";
import { Form } from "react-bootstrap";

export default function PromoCodeAddEditModal({ isOpen, onClose, onSave, promoCode, tickets, isSaving }) {
  const { t } = useTranslation("ticketingDashboard");

  const [formData, setFormData] = useState({
    name: "",
    code: "",
    maxUsage: 0,
    ticketDiscounts: [],
    isActive: true,
    isPartnerCode: false,
    excludeFromTheoreticalRevenue: false,
    reserveTicketCapacity: false,
    reservedTicketQuantities: {},
  });

  const [errors, setErrors] = useState({});

  // Include option supplements when validating fixed-amount discounts.
  // Ticket eligibility itself is independent of its price.
  const getMaximumConfiguredPrice = (ticket = {}) => {
    const optionMaximum = (ticket.options || []).reduce((total, group) => {
      const positivePrices = (group.values || [])
        .map((value) => Math.max(0, Number(value?.priceDelta || 0)))
        .sort((left, right) => right - left);
      if (group.selectionType === "multi") {
        const limit = Number(group.maxSelect || 0);
        return total + positivePrices.slice(0, limit > 0 ? limit : positivePrices.length).reduce(
          (sum, value) => sum + value,
          0,
        );
      }
      return total + (positivePrices[0] || 0);
    }, 0);
    return Number(ticket.price || 0) + optionMaximum;
  };

  const isDiscountValueValid = (discount, ticket) => {
    const value = Number(discount?.discountValue);
    if (discount?.discountValue === "" || discount?.discountValue == null || !Number.isFinite(value) || value < 0 || (!formData.isPartnerCode && value === 0)) return false;
    return discount.isPercentage === false
      ? value <= getMaximumConfiguredPrice(ticket)
      : value <= 100;
  };

  useEffect(() => {
    if (promoCode) {
      setFormData({
        name: promoCode.name || "",
        code: promoCode.code || "",
        maxUsage: promoCode.maxUsage === -1 ? 0 : promoCode.maxUsage || 0,
        ticketDiscounts:
          promoCode.ticketDiscounts?.map((discount) => ({
            ...discount,
            discountValue: discount.discountValue ?? discount.discountPercentage ?? (promoCode.isPartnerCode ? 100 : ""),
            isPercentage: discount.isPercentage ?? true,
          })) || [],
        isActive: promoCode.isActive !== undefined ? promoCode.isActive : true,
        isPartnerCode: promoCode.isPartnerCode ?? false,
        excludeFromTheoreticalRevenue: promoCode.excludeFromTheoreticalRevenue ?? false,
        reserveTicketCapacity: promoCode.reserveTicketCapacity ?? false,
        reservedTicketQuantities: promoCode.reservedTicketQuantities || {},
      });
    } else {
      setFormData({
        name: "",
        code: "",
        maxUsage: 0,
        ticketDiscounts: [],
        isActive: true,
        isPartnerCode: false,
        excludeFromTheoreticalRevenue: false,
        reserveTicketCapacity: false,
        reservedTicketQuantities: {},
      });
    }
    setErrors({});
  }, [promoCode, isOpen]);

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = t("promo.errors.nameRequired");
    }

    if (!formData.code.trim()) {
      newErrors.code = t("promo.errors.codeRequired");
    } else if (formData.code.length < 3) {
      newErrors.code = t("promo.errors.codeMinLength");
    }

    if (formData.maxUsage < 0) {
      newErrors.maxUsage = t("promo.errors.invalidMaxUsage");
    }

    if (formData.reserveTicketCapacity) {
      const reservationTarget = getReservationTarget();
      const reservationTotal = Object.values(formData.reservedTicketQuantities || {}).reduce(
        (total, quantity) => total + Number(quantity || 0),
        0,
      );
      if (Number(formData.maxUsage || 0) === 0) {
        newErrors.reservations = t("promo.errors.reservationNeedsLimit");
      } else if (reservationTotal !== reservationTarget) {
        newErrors.reservations = t("promo.errors.reservationTotal", { count: reservationTarget });
      }
    }

    const validDiscounts = formData.ticketDiscounts.filter((discount) => {
      const ticket = tickets.find((item) => item.id === discount.ticketId);
      return ticket && isDiscountValueValid(discount, ticket);
    });

    if (validDiscounts.length === 0) {
      newErrors.ticketDiscounts = t("promo.errors.noDiscounts");
    }

    return newErrors;
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => {
      const nextFormData = { ...prev, [field]: value };

      // With one discounted ticket there is no allocation choice: keep its
      // reservation aligned with the remaining number of code usages.
      if (field === "maxUsage" && prev.reserveTicketCapacity && prev.ticketDiscounts.length === 1) {
        nextFormData.reservedTicketQuantities = {
          ...prev.reservedTicketQuantities,
          [prev.ticketDiscounts[0].ticketId]: Math.max(
            0,
            Number(value || 0) - Number(promoCode?.currentUsage || 0),
          ),
        };
      }

      return nextFormData;
    });

    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: null,
      }));
    }
  };

  const handleTicketDiscountChange = (ticketId, discountValue, ticketPrice, isPercentage) => {
    const value = discountValue.trim();

    setFormData((prev) => {
      const newTicketDiscounts = [...prev.ticketDiscounts];
      const existingIndex = newTicketDiscounts.findIndex((td) => td.ticketId === ticketId);

      if (value === "" || (value === "0" && !formData.isPartnerCode)) {
        if (existingIndex !== -1) {
          newTicketDiscounts[existingIndex] = { ticketId, discountValue: "", isPercentage };
        }
      } else {
        const discount = Number(value);
        const validationCondition = isPercentage
          ? discount >= (formData.isPartnerCode ? 0 : 1) && discount <= 100
          : discount > 0 && discount <= ticketPrice;

        if (validationCondition) {
          if (existingIndex !== -1) {
            newTicketDiscounts[existingIndex] = {
              ticketId,
              discountValue: discount,
              isPercentage,
            };
          } else {
            newTicketDiscounts.push({ ticketId, discountValue: discount, isPercentage });
          }
        }
      }

      return {
        ...prev,
        ticketDiscounts: newTicketDiscounts,
      };
    });
  };

  const handleDiscountTypeChange = (ticketId, isPercentage) => {
    setFormData((prev) => {
      const newTicketDiscounts = prev.ticketDiscounts.map((td) =>
        td.ticketId === ticketId ? { ...td, isPercentage, discountValue: "" } : td,
      );

      return { ...prev, ticketDiscounts: newTicketDiscounts };
    });
  };

  const handleRemoveTicketDiscount = (ticketId) => {
    setFormData((prev) => {
      const reservations = { ...(prev.reservedTicketQuantities || {}) };
      delete reservations[ticketId];
      return {
        ...prev,
        ticketDiscounts: prev.ticketDiscounts.filter((td) => td.ticketId !== ticketId),
        reservedTicketQuantities: reservations,
      };
    });
  };

  const handleAddTicketDiscount = (ticketId) => {
    if (!formData.ticketDiscounts.find((td) => td.ticketId === ticketId)) {
      setFormData((prev) => {
        const ticketDiscounts = [
          ...prev.ticketDiscounts,
          { ticketId, discountValue: prev.isPartnerCode ? 100 : 10, isPercentage: true },
        ];
        const nextFormData = { ...prev, ticketDiscounts };

        // If reservation was enabled before a ticket was selected, assign all
        // remaining uses to this first ticket by default.
        if (prev.reserveTicketCapacity && ticketDiscounts.length === 1) {
          nextFormData.reservedTicketQuantities = {
            ...prev.reservedTicketQuantities,
            [ticketId]: Math.max(
              0,
              Number(prev.maxUsage || 0) - Number(promoCode?.currentUsage || 0),
            ),
          };
        }

        return nextFormData;
      });
    }
  };

  const handlePartnerChange = (e) => {
    if (e.target.checked) {
      setFormData({
        ...formData,
        ticketDiscounts: formData.ticketDiscounts.map((ticketDiscount) => ({
          ...ticketDiscount,
          discountValue: ticketDiscount.isPercentage !== false ? (ticketDiscount.discountValue ?? 100) : 100,
          isPercentage: true,
        })),
        isPartnerCode: true,
      });
    } else {
      setFormData((prev) => ({
        ...prev,
        isPartnerCode: false,
        excludeFromTheoreticalRevenue: false,
      }));
    }
  };

  const getReservationTarget = () =>
    Math.max(0, Number(formData.maxUsage || 0) - Number(promoCode?.currentUsage || 0));

  const getReservedQuantity = (ticketId) =>
    Number(formData.reservedTicketQuantities?.[ticketId] || 0);

  const handleReservationChange = (isEnabled) => {
    setFormData((prev) => {
      if (!isEnabled) {
        return { ...prev, reserveTicketCapacity: false, reservedTicketQuantities: {} };
      }

      const target = Math.max(0, Number(prev.maxUsage || 0) - Number(promoCode?.currentUsage || 0));
      const ticketIds = prev.ticketDiscounts.map((discount) => discount.ticketId);
      const reservations = { ...(prev.reservedTicketQuantities || {}) };
      if (ticketIds.length === 1 && !reservations[ticketIds[0]]) {
        reservations[ticketIds[0]] = target;
      }
      return { ...prev, reserveTicketCapacity: true, reservedTicketQuantities: reservations };
    });
  };

  const handleReservedQuantityChange = (ticketId, value) => {
    const quantity = Math.max(0, Number.parseInt(value, 10) || 0);
    setFormData((prev) => ({
      ...prev,
      reservedTicketQuantities: {
        ...prev.reservedTicketQuantities,
        [ticketId]: quantity,
      },
    }));
  };

  const getAvailableTickets = () => {
    return tickets.filter(
      (ticket) => !formData.ticketDiscounts.find((td) => td.ticketId === ticket.id),
    );
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    let validTicketDiscounts = formData.ticketDiscounts.filter((discount) => {
      const ticket = tickets.find((item) => item.id === discount.ticketId);
      return ticket && isDiscountValueValid(discount, ticket);
    });


    const promoCodeData = {
      ...formData,
      code: formData.code.toUpperCase(),
      maxUsage: formData.maxUsage === 0 ? 0 : parseInt(formData.maxUsage),
      ticketDiscounts: validTicketDiscounts,
      excludeFromTheoreticalRevenue:
        formData.isPartnerCode && formData.excludeFromTheoreticalRevenue,
      reservedTicketQuantities: formData.reserveTicketCapacity
        ? formData.reservedTicketQuantities
        : {},
    };

    if (promoCode) {
      promoCodeData.id = promoCode.id;
      promoCodeData.currentUsage = promoCode.currentUsage;
    }

    onSave(promoCodeData);
  };

  const handleClose = () => {
    setFormData({
      name: "",
      code: "",
      maxUsage: 0,
      ticketDiscounts: [],
      isActive: true,
      isPartnerCode: false,
      excludeFromTheoreticalRevenue: false,
      reserveTicketCapacity: false,
      reservedTicketQuantities: {},
    });
    setErrors({});
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="promo-code-modal-overlay" onClick={handleOverlayClick}>
      <div className="promo-code-modal">
        <div className="modal-header">
          <h3>{promoCode ? t("promo.updatePromoCode") : t("promo.createPromoCode")}</h3>
          <button className="close-btn" onClick={handleClose}>
            <img src={closeIcon} alt={t("close")} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="modal-form">
          <div className="modal-body">
            <div className="form-group">
              <label>{t("partnerLink")}</label>
              <div className="add-partner-section">
                <p>{t("partnerQuestion")}</p>
                <Form.Check
                  type="switch"
                  checked={formData.isPartnerCode}
                  onChange={handlePartnerChange}
                />
              </div>
            </div>

            {formData.isPartnerCode && (
              <div className="form-group promo-theoretical-revenue-section">
                <label>{t("promo.excludeFromTheoreticalRevenue")}</label>
                <div className="add-partner-section">
                  <p>{t("promo.excludeFromTheoreticalRevenueHint")}</p>
                  <Form.Check
                    type="switch"
                    checked={formData.excludeFromTheoreticalRevenue}
                    onChange={(e) =>
                      handleInputChange("excludeFromTheoreticalRevenue", e.target.checked)
                    }
                    aria-label={t("promo.excludeFromTheoreticalRevenue")}
                  />
                </div>
              </div>
            )}

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="promo-name">{t("promo.name")}</label>
                <input
                  id="promo-name"
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange("name", e.target.value)}
                  className={errors.name ? "error" : ""}
                />
                {errors.name && <span className="error-text">{errors.name}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="promo-code">{t("promo.code")}</label>
                <input
                  id="promo-code"
                  type="text"
                  value={formData.code}
                  onChange={(e) => handleInputChange("code", e.target.value.toUpperCase())}
                  className={errors.code ? "error" : ""}
                />
                {errors.code && <span className="error-text">{errors.code}</span>}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="max-usage">{t("promo.maxUsage")}</label>
                <input
                  id="max-usage"
                  type="number"
                  min="0"
                  value={formData.maxUsage}
                  onChange={(e) => handleInputChange("maxUsage", parseInt(e.target.value) || 0)}
                  placeholder={t("promo.maxUsagePlaceholder")}
                  className={errors.maxUsage ? "error" : ""}
                />
                <small className="form-text">{t("promo.maxUsageHint")}</small>
                {errors.maxUsage && <span className="error-text">{errors.maxUsage}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="is-active">{t("promo.status")}</label>
                <select
                  id="is-active"
                  value={formData.isActive}
                  onChange={(e) => handleInputChange("isActive", e.target.value === "true")}
                >
                  <option value={true}>{t("promo.active")}</option>
                  <option value={false}>{t("promo.inactive")}</option>
                </select>
              </div>
            </div>

            <div className="form-group promo-reservation-section">
              <div className="add-partner-section">
                <div>
                  <label htmlFor="reserve-ticket-capacity">{t("promo.reserveTicketCapacity")}</label>
                  <p>{t("promo.reserveTicketCapacityHint")}</p>
                </div>
                <Form.Check
                  id="reserve-ticket-capacity"
                  type="switch"
                  checked={formData.reserveTicketCapacity}
                  disabled={Number(formData.maxUsage || 0) === 0}
                  onChange={(event) => handleReservationChange(event.target.checked)}
                />
              </div>

              {formData.reserveTicketCapacity && (
                <div className="reservation-quantities">
                  <p className="reservation-total">
                    {t("promo.reservationTarget", { count: getReservationTarget() })}
                  </p>
                  {formData.ticketDiscounts.map((discount) => {
                    const ticket = tickets.find((item) => item.id === discount.ticketId);
                    if (!ticket) return null;
                    return (
                      <label key={discount.ticketId} className="reservation-quantity-row">
                        <span>{ticket.name}</span>
                        <input
                          type="number"
                          min="0"
                          step="1"
                          value={getReservedQuantity(discount.ticketId)}
                          onChange={(event) =>
                            handleReservedQuantityChange(discount.ticketId, event.target.value)
                          }
                          aria-label={t("promo.reservedQuantity", { ticket: ticket.name })}
                        />
                      </label>
                    );
                  })}
                </div>
              )}
              {errors.reservations && <span className="error-text">{errors.reservations}</span>}
            </div>

            <div className="form-group">
              <label>{t("promo.ticketDiscounts")}</label>
              {formData.ticketDiscounts?.length > 0 && (
                <div className="ticket-discounts-form">
                  {formData.ticketDiscounts.map((discount) => {
                    const ticket = tickets.find((t) => t.id === discount.ticketId);
                    if (!ticket) return null;

                    return (
                      <div key={discount.ticketId} className="ticket-discount-row">
                        <div className="ticket-info">
                          <span className="ticket-name">{ticket.name}</span>
                          <span className="ticket-price">
                            {ticket.price}€
                            {getMaximumConfiguredPrice(ticket) > Number(ticket.price || 0)
                              ? t("promo.optionsUpTo", { price: getMaximumConfiguredPrice(ticket) })
                              : ""}
                          </span>
                        </div>
                        <div className="discount-input">
                          <input
                            type="number"
                            min={formData.isPartnerCode ? 0 : 1}
                            max={
                              discount.isPercentage
                                ? 100
                                : getMaximumConfiguredPrice(ticket)
                            }
                            step={discount.isPercentage ? 1 : 0.01}
                            value={discount.discountValue}
                            disabled={isSaving}
                            onChange={(e) =>
                              handleTicketDiscountChange(
                                discount.ticketId,
                                e.target.value,
                                getMaximumConfiguredPrice(ticket),
                                discount.isPercentage,
                              )
                            }
                            placeholder="10"
                          />
                          <button
                            className={`${discount.isPercentage ? "active" : ""} discount-type-btn`}
                            disabled={formData.isPartnerCode}
                            onClick={() => handleDiscountTypeChange(discount.ticketId, true)}
                            type="button"
                          >
                            %
                          </button>
                          {!formData.isPartnerCode && (
                            <button
                              className={`${!discount.isPercentage ? "active" : ""} discount-type-btn`}
                              disabled={formData.isPartnerCode}
                              onClick={() => handleDiscountTypeChange(discount.ticketId, false)}
                              type="button"
                            >
                              €
                            </button>
                          )}

                          <button
                            type="button"
                            className="remove-discount-btn"
                            onClick={() => handleRemoveTicketDiscount(discount.ticketId)}
                            title={t("promo.removeDiscount")}
                          >
                            <img src={deleteIcon} alt={t("promo.removeDiscount")} />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              {getAvailableTickets().length > 0 && (
                <div className="add-discount-section">
                  <select
                    className="add-discount-select"
                    size={Math.min(6, getAvailableTickets().length)}
                    onChange={(e) => {
                      if (e.target.value) {
                        handleAddTicketDiscount(e.target.value);
                        e.target.value = "";
                      }
                    }}
                    value=""
                  >
                    <option value="">{t("promo.selectTicketToDiscount")}</option>
                    {getAvailableTickets().map((ticket) => (
                      <option key={ticket.id} value={ticket.id}>
                        {ticket.name} - {ticket.price}€
                        {getMaximumConfiguredPrice(ticket) > Number(ticket.price || 0)
                          ? t("promo.optionsUpTo", { price: getMaximumConfiguredPrice(ticket) })
                          : ""}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              {errors.ticketDiscounts && (
                <span className="error-text">{errors.ticketDiscounts}</span>
              )}
            </div>
          </div>

          <div className="modal-actions">
            <button type="button" className="cancel-btn" onClick={handleClose} disabled={isSaving}>
              {t("cancel")}
            </button>
            <button type="submit" className="primary-btn" disabled={isSaving}>
              {isSaving ? t("promo.loading") : promoCode ? t("promo.update") : t("promo.create")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
